// Printable A4 flashcards. Teachers / parents pick a word pack and print
// the deck — kids see picture + English word, perfect for a pre-game
// vocab review. Designed to fit 6 cards per A4 sheet (2 cols × 3 rows)
// with safe cut margins. The screen UI hides on print so only the cards
// hit the page.
//
// Triggered from the level picker via the new "🖨 Flashcards" button.

import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getWordPack, WORD_PACKS } from "../data/word-packs";
import { GameImage } from "../game/ui/GameImage";

export function Flashcards() {
  const { packId } = useParams<{ packId: string }>();
  const initialPack = packId ? getWordPack(packId) : WORD_PACKS[0];
  const [selectedId, setSelectedId] = useState<string>(initialPack?.id ?? WORD_PACKS[0]!.id);
  const pack = useMemo(() => getWordPack(selectedId), [selectedId]);

  if (!pack) {
    return (
      <div className="flashcards-page">
        <p>Pack not found.</p>
        <Link to="/play">← Back</Link>
      </div>
    );
  }

  // Chunk words into pages of 6 so each printed sheet is a clean A4.
  const pages: typeof pack.words[] = [];
  for (let i = 0; i < pack.words.length; i += 6) {
    pages.push(pack.words.slice(i, i + 6));
  }
  // Single pack of 5 words → one page with 5 cards (an empty slot left).
  // Multi-page packs round up naturally.

  return (
    <div className="flashcards-page">
      {/* On-screen controls (hidden by print CSS). */}
      <div className="flashcards-toolbar">
        <Link to="/play" className="btn btn-ghost">← Back</Link>
        <h1>Flashcards · {pack.title}</h1>
        <label className="flashcards-pack-picker">
          <span>Pack:</span>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            {WORD_PACKS.sort((a, b) => a.orderIndex - b.orderIndex).map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </label>
        <button className="btn btn-primary" onClick={() => window.print()}>
          🖨 Print
        </button>
      </div>

      <p className="t-muted flashcards-hint">
        Use your browser's print dialog (or the button above). Set the paper
        size to <strong>A4</strong> and turn background graphics ON so the
        word panel prints.
      </p>

      {/* Print-ready sheets. */}
      {pages.map((words, pageIdx) => (
        <section key={pageIdx} className="flashcard-sheet">
          {words.map((w) => (
            <article key={w.id} className="flashcard">
              <div className="flashcard-image">
                <GameImage
                  src={w.imageUrl ?? undefined}
                  alt={w.english}
                  className="flashcard-image-inner"
                />
              </div>
              <div className="flashcard-word">{w.english}</div>
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}
